
import { AppMessageActions } from "./app-message-actions.js";
import { AutoMarkdown } from "./auto-markdown.js";

const styles = css`
  :host { display: block; min-width: 0; width: 100%; }
  .row { display: flex; min-width: 0; width: 100%; }
  .row.user { justify-content: flex-end; }
  .message { min-width: 0; overflow-wrap: anywhere; }
  .agent { padding: 2px 0; width: 100%; }
  .rendered p { margin: 0 0 0.75em; }
  .rendered p:last-child { margin-bottom: 0; }
  .rendered h1, .rendered h2, .rendered h3, .rendered h4, .rendered h5, .rendered h6 { font-weight: 600; line-height: 1.3; margin: 0.5em 0 0.25em; }
  .rendered h1:first-child, .rendered h2:first-child, .rendered h3:first-child, .rendered h4:first-child, .rendered h5:first-child, .rendered h6:first-child { margin-top: 0; }
  .rendered h1 { font-size: 1.6em; }
  .rendered h2 { font-size: 1.4em; }
  .rendered h3 { font-size: 1.2em; }
  .rendered ul, .rendered ol { margin: 0 0 0.75em; padding-left: 1.5em; }
  .rendered li { margin: 0.2em 0; }
  .rendered blockquote { border-left: 3px solid var(--border); color: var(--muted-foreground); margin: 0 0 0.75em; padding-left: 1em; }
  .rendered pre { background: var(--muted); border-radius: 6px; margin: 0.75em 0; max-width: 100%; overflow-x: auto; padding: 0.8em 1em; }
  .rendered code { background: var(--muted); border-radius: 4px; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 0.92em; padding: 0.1em 0.25em; }
  .rendered pre code { background: transparent; border-radius: 0; padding: 0; }
  .rendered .table-wrap { margin: 0.75em 0; max-width: 100%; overflow-x: auto; }
  .rendered table { border-collapse: collapse; font-size: 0.95em; inline-size: max-content; min-inline-size: 100%; }
  .rendered th, .rendered td { border: 1px solid var(--border); max-inline-size: 24rem; min-inline-size: 10rem; overflow-wrap: break-word; padding: 0.4em 0.75em; text-align: left; vertical-align: top; white-space: normal; }
  .rendered th { background: var(--muted); font-weight: 600; }
  .rendered img, .rendered video, .rendered audio, .rendered iframe { display: block; height: auto; max-width: 100%; }
  .rendered iframe { border: 0; box-sizing: border-box; overflow: hidden; width: 100%; }
  .rendered a { color: var(--primary); text-decoration: none; }
  .rendered a:hover { text-decoration: underline; }
  .user .message { background: var(--message-user-bg); border: 1px solid color-mix(in oklab, var(--message-user-bg) 82%, var(--border)); border-radius: 16px; color: var(--message-user-fg); max-width: min(80%, 640px); padding: 12px; }
  .plain { white-space: pre-wrap; }
  .actions { margin-top: 6px; }
  .user-actions { max-width: min(80%, 640px); width: 100%; }
  @media (max-width: 767px) { .user .message, .user-actions { max-width: 88%; } }
  @media print {
    .user .message { background: transparent; border: 0; border-left: 3px solid #999; border-radius: 0; color: inherit; max-width: 100%; padding: 0 0 0 14px; }
    app-message-actions { display: none; }
  }
`;

export class AppMessage extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppMessage)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["messageid","data-message-id","seq","data-seq","role","data-role","kind","data-kind","format","data-format","text","data-text","pending","data-pending"];

  #messageId = "";
  #seq = 0;
  #role = "user";
  #kind = "user";
  #format = "markdown";
  #text = "";
  #pending = false;
  #messageIdSet = false;
  #seqSet = false;
  #roleSet = false;
  #kindSet = false;
  #formatSet = false;
  #textSet = false;
  #pendingSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #attr0;
  #attr1;
  #attrLast0;
  #attrLast1;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get messageId() {
    return this.#messageId;
  }

  set messageId(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#messageId, next)) return;
    this.#messageId = next;
    this.#messageIdSet = true;
    this.#updateBindings('messageId');
  }

  get seq() {
    return this.#seq;
  }

  set seq(value) {
    const raw = value ?? 0;
    const next = raw == null ? raw : Number(raw);
    if (Object.is(this.#seq, next)) return;
    this.#seq = next;
    this.#seqSet = true;
    this.#updateBindings('seq');
  }

  get role() {
    return this.#role;
  }

  set role(value) {
    const raw = value ?? "user";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#role, next)) return;
    this.#role = next;
    this.#roleSet = true;
    this.#updateBindings('role');
  }

  get kind() {
    return this.#kind;
  }

  set kind(value) {
    const raw = value ?? "user";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#kind, next)) return;
    this.#kind = next;
    this.#kindSet = true;
    this.#updateBindings('kind');
  }

  get format() {
    return this.#format;
  }

  set format(value) {
    const raw = value ?? "markdown";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#format, next)) return;
    this.#format = next;
    this.#formatSet = true;
    this.#updateBindings('format');
  }

  get text() {
    return this.#text;
  }

  set text(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#text, next)) return;
    this.#text = next;
    this.#textSet = true;
    this.#updateBindings('text');
  }

  get pending() {
    return this.#pending;
  }

  set pending(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#pending, next)) return;
    this.#pending = next;
    this.#pendingSet = true;
    this.#updateBindings('pending');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-message-id')) this.messageId = this.dataset.messageId;
    if (this.hasAttribute('data-seq')) this.seq = this.dataset.seq;
    if (this.hasAttribute('data-role')) this.role = this.dataset.role;
    if (this.hasAttribute('data-kind')) this.kind = this.dataset.kind;
    if (this.hasAttribute('data-format')) this.format = this.dataset.format;
    if (this.hasAttribute('data-text')) this.text = this.dataset.text;
    if (this.hasAttribute('data-pending')) this.pending = this.dataset.pending;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.messageId = props.messageId;
      this.seq = props.seq;
      this.role = props.role;
      this.kind = props.kind;
      this.format = props.format;
      this.text = props.text;
      this.pending = props.pending;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#messageIdSet) this.messageId = this.dataset.messageId;
    if (!this.#seqSet) this.seq = this.dataset.seq;
    if (!this.#roleSet) this.role = this.dataset.role;
    if (!this.#kindSet) this.kind = this.dataset.kind;
    if (!this.#formatSet) this.format = this.dataset.format;
    if (!this.#textSet) this.text = this.dataset.text;
    if (!this.#pendingSet) this.pending = this.dataset.pending;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'messageid':
      case 'data-message-id':
        this.messageId = newValue;
        break;
      case 'seq':
      case 'data-seq':
        this.seq = newValue;
        break;
      case 'role':
      case 'data-role':
        this.role = newValue;
        break;
      case 'kind':
      case 'data-kind':
        this.kind = newValue;
        break;
      case 'format':
      case 'data-format':
        this.format = newValue;
        break;
      case 'text':
      case 'data-text':
        this.text = newValue;
        break;
      case 'pending':
      case 'data-pending':
        this.pending = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    const user = this.#kind === "user";
    return `<style>${this.#escText(styles)}</style><div class="${this.#escAttr(`row${user ? " user" : ""}`)}" data-argon-bind="0"><div class="${this.#escAttr(`message ${user ? "user-message" : "agent"}`)}" data-argon-bind="1"><!--argon-text:0-->${(this.#kind === "agent") ? `<auto-markdown data-text="${this.#attrEsc(this.#text)}" data-format="${this.#attrEsc(this.#format)}">${""}<div class="rendered">${(this.#format === "html") ? this.#text : this.#escText(this.#text)}</div></auto-markdown>` : `<div class="plain">${this.#escText(this.#text)}</div>`}<!--/argon-text:0--></div></div><!--argon-text:1-->${((!this.#pending) && (this.#text !== "")) ? `<div class="${this.#escAttr(`actions${user ? " user-actions" : ""}`)}" style="${this.#escAttr(user ? "margin-left: auto" : "")}"><app-message-actions data-text="${this.#attrEsc(this.#text)}" data-align="${this.#attrEsc(user ? "end" : "start")}">${""}</app-message-actions></div>` : ""}<!--/argon-text:1-->`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const user = (prop === undefined || prop === 'pending' || prop === 'text' || prop === 'kind') ? (this.#kind === "user") : undefined;
    if (prop === undefined || prop === 'kind' || prop === 'text' || prop === 'format') {
      const __argonNext = `${(this.#kind === "agent") ? `<auto-markdown data-text="${this.#attrEsc(this.#text)}" data-format="${this.#attrEsc(this.#format)}">${""}<div class="rendered">${(this.#format === "html") ? this.#text : this.#escText(this.#text)}</div></auto-markdown>` : `<div class="plain">${this.#escText(this.#text)}</div>`}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'pending' || prop === 'text' || prop === 'kind') {
      const __argonNext = `${((!this.#pending) && (this.#text !== "")) ? `<div class="${this.#escAttr(`actions${user ? " user-actions" : ""}`)}" style="${this.#escAttr(user ? "margin-left: auto" : "")}"><app-message-actions data-text="${this.#attrEsc(this.#text)}" data-align="${this.#attrEsc(user ? "end" : "start")}">${""}</app-message-actions></div>` : ""}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'kind') && this.#attr0) {
      const __argonNext = `${`row${user ? " user" : ""}`}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('class', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'kind') && this.#attr1) {
      const __argonNext = `${`message ${user ? "user-message" : "agent"}`}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr1.setAttribute('class', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;

    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
